// src/components/Dashboard/KpiStatsCards.tsx
import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import { ExpandMore, ExpandLess, Info } from '@mui/icons-material';
import {
  getUserKpiStats,
  getAirportKpiStats,
  getSystemKpiStats,
  DashboardApiError,
} from '../../services/dashboardApi';
import { isIntegerUserId } from '../../services/authApi';
import { useModalOpen } from '../../contexts/ModalOpenContext';
import type {
  UserKpiStatsResponse,
  AirportKpiStatsResponse,
  SystemKpiStatsResponse,
} from '../../services/dashboardApi';

interface KpiStatsCardsProps {
  type: 'user' | 'airport' | 'system';
  userId?: number | string; // May be GUID (string) or integer (number)
  airportId?: number;
}

export default function KpiStatsCards({
  type,
  userId,
  airportId,
}: KpiStatsCardsProps) {
  const { isModalOpen } = useModalOpen();
  const [stats, setStats] = useState<
    UserKpiStatsResponse | AirportKpiStatsResponse | SystemKpiStatsResponse | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false); // Default minimized

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        setError(null);

        // For airport type, wait for airportId to be available
        if (type === 'airport' && !airportId) {
          // Don't show error immediately - airportId might still be loading
          // Keep loading state until airportId is available
          setLoading(true);
          return;
        }

        let response;
        if (type === 'user' && userId && isIntegerUserId(userId)) {
          response = await getUserKpiStats(userId);
        } else if (type === 'user' && userId && typeof userId === 'string') {
          // userId is a GUID - backend should return integer userId, but fell back to GUID
          // This can happen if user doesn't exist in Users table (fallback behavior)
          setError('User KPI stats unavailable: userId format not supported (GUID fallback)');
          setLoading(false);
          return;
        } else if (type === 'airport' && airportId) {
          response = await getAirportKpiStats(airportId);
        } else if (type === 'system') {
          response = await getSystemKpiStats();
        } else {
          // Only show error if we have all required info but still can't proceed
          if (type === 'airport') {
            // Airport type but no airportId - might still be loading
            setLoading(true);
            return;
          }
          setError('Missing required parameters');
          setLoading(false);
          return;
        }

        setStats(response);
      } catch (err) {
        const apiError = err as DashboardApiError;
        setError(apiError.message || 'Failed to load KPI stats');
        console.error('Error loading KPI stats:', err);
      } finally {
        setLoading(false);
      }
    };

    loadStats();

    // Auto-refresh every 5 minutes; Sprint B1: skip when a modal is open
    if (!(type === 'airport' && !airportId)) {
      const interval = setInterval(() => {
        if (!isModalOpen) loadStats();
      }, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [type, userId, airportId, isModalOpen]);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No KPI stats available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Render user stats
  if (type === 'user' && 'totalTasks' in stats) {
    const userStats = stats as UserKpiStatsResponse;
    return (
      <Card>
        <CardContent sx={{ p: expanded ? 2 : 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: expanded ? 1 : 0 }}>
            <Typography variant="h6">Performance Metrics</Typography>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          
          {/* Minimized View - Key Metrics Only */}
          {!expanded && (
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {userStats.totalTasks}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {userStats.completionRate.toFixed(0)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Complete
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 'bold',
                      color: userStats.averageKpiScore > 0 ? 'error.main' : 'success.main',
                    }}
                  >
                    {userStats.averageKpiScore > 0 ? '+' : ''}
                    {userStats.averageKpiScore.toFixed(1)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg KPI
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Expanded View - Full Details */}
          <Collapse in={expanded}>
            <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {userStats.totalTasks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Tasks
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {userStats.completedTasks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {userStats.completionRate.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completion Rate
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    color: userStats.averageKpiScore > 0 ? 'error.main' : 'success.main',
                  }}
                >
                  {userStats.averageKpiScore > 0 ? '+' : ''}
                  {userStats.averageKpiScore.toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg KPI Score
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  {userStats.tasksOnTime}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  On Time
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  {userStats.tasksBehind}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Behind
                </Typography>
              </Box>
            </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>
    );
  }

  // Render airport stats
  if (type === 'airport' && 'totalProjects' in stats) {
    const airportStats = stats as AirportKpiStatsResponse;
    return (
      <Card>
        <CardContent sx={{ p: expanded ? 2 : 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: expanded ? 1 : 0 }}>
            <Typography variant="h6">Airport Metrics</Typography>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          
          {/* Minimized View */}
          {!expanded && (
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {airportStats.totalProjects}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Projects
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {airportStats.activeProjects}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 'bold',
                      color: airportStats.averageKpiScore > 0 ? 'error.main' : 'success.main',
                    }}
                  >
                    {airportStats.averageKpiScore > 0 ? '+' : ''}
                    {airportStats.averageKpiScore.toFixed(1)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg KPI
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Expanded View */}
          <Collapse in={expanded}>
            <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {airportStats.totalProjects}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Projects
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {airportStats.activeProjects}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Projects
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {airportStats.totalTasks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Tasks
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {airportStats.completionRate.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completion Rate
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    color: airportStats.averageKpiScore > 0 ? 'error.main' : 'success.main',
                  }}
                >
                  {airportStats.averageKpiScore > 0 ? '+' : ''}
                  {airportStats.averageKpiScore.toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg KPI Score
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  {airportStats.projectsOnTrack}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  On Track
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  {airportStats.projectsAtRisk}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  At Risk
                </Typography>
              </Box>
            </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>
    );
  }

  // Render system stats
  if (type === 'system' && 'totalProjects' in stats) {
    const systemStats = stats as SystemKpiStatsResponse;
    return (
      <Card>
        <CardContent sx={{ p: expanded ? 2 : 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: expanded ? 1 : 0 }}>
            <Typography variant="h6">System Metrics</Typography>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          
          {/* Minimized View */}
          {!expanded && (
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {systemStats.totalProjects}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Projects
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {systemStats.activeUsers}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Users
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 'bold',
                      color: systemStats.averageKpiScore > 0 ? 'error.main' : 'success.main',
                    }}
                  >
                    {systemStats.averageKpiScore > 0 ? '+' : ''}
                    {systemStats.averageKpiScore.toFixed(1)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg KPI
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: systemStats.systemHealthScore >= 90 ? 'success.main' : 'warning.main',
                      }}
                    >
                      {systemStats.systemHealthScore}
                    </Typography>
                    <Tooltip 
                      title="System Health Score: A composite metric (0-100) that evaluates overall system performance based on project completion rates, task status, system availability, and operational efficiency. Scores of 90+ indicate excellent health, while scores below 90 may indicate areas needing attention."
                      arrow
                      placement="top"
                    >
                      <IconButton size="small" sx={{ p: 0.5, color: 'text.secondary' }}>
                        <Info sx={{ fontSize: '0.875rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Health
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Expanded View */}
          <Collapse in={expanded}>
            <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {systemStats.totalProjects}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Projects
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {systemStats.activeProjects}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Projects
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {systemStats.totalTasks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Tasks
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {systemStats.completionRate.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completion Rate
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 'bold',
                    color: systemStats.averageKpiScore > 0 ? 'error.main' : 'success.main',
                  }}
                >
                  {systemStats.averageKpiScore > 0 ? '+' : ''}
                  {systemStats.averageKpiScore.toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg KPI Score
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {systemStats.activeUsers}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Users
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={4}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 'bold',
                      color: systemStats.systemHealthScore >= 90 ? 'success.main' : 'warning.main',
                    }}
                  >
                    {systemStats.systemHealthScore}
                  </Typography>
                  <Tooltip 
                    title="System Health Score: A composite metric (0-100) that evaluates overall system performance based on project completion rates, task status, system availability, and operational efficiency. Scores of 90+ indicate excellent health, while scores below 90 may indicate areas needing attention."
                    arrow
                    placement="top"
                  >
                    <IconButton size="small" sx={{ p: 0.5, color: 'text.secondary' }}>
                      <Info sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Health Score
                </Typography>
              </Box>
            </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>
    );
  }

  return null;
}

